/**
 * How a dashboard grid is projected onto the current screen (card t_b396f5df).
 *
 * The grid layout is authored on a desktop against a fixed-ratio canvas (12
 * columns, 16:9 by default) and every widget is placed with its raw authored
 * `col / row / colSpan / rowSpan`. Projected onto a phone that canvas becomes
 * ~360x200 CSS px with 4 columns — measured on the tablet: 8 x 74px columns and
 * 10 x 29px rows, i.e. widget boxes of 32x29 px — which is the owner's
 * "dashboards is crushed on mobile - width is too small. all widgets on same
 * screen".
 *
 * So a narrow screen gets a different SHAPE, not a scaled canvas: one column of
 * full-width cards in authored reading order. This module owns that decision as
 * a pure function, because the breakpoints alone cannot express it: the tablet
 * the owner judges on is 800x1333 CSS px, so `md:` (768) matches in portrait too
 * and width alone can never tell its orientations apart.
 *
 * Rules:
 *  - below 768 px: a phone, either orientation -> list;
 *  - 768..1023 px in portrait: a tablet held upright -> list (the canvas is
 *    ~768x432 there, which is the measured crush);
 *  - everything else (tablet landscape, desktop) -> canvas, exactly as before.
 *
 * A canvas layout asks the same rule (card t_9adb47cf). The floorplan keeps its
 * reference space (1920x1080 by default) and opens at the fit `computeFitState`
 * computes, so on a 390x844 phone the canvas paints 390x219 inside a 390x739
 * wrapper and its widgets come out 23x23 px — smaller than the grid's crush, with
 * 12px authored labels painted at 2.4px.
 *
 * The floorplan's answer is deliberately NOT this list surface (owner's report on
 * the Rožna S1 plan, cards t_9adb47cf -> this change): a plan is a picture of a
 * building, and stacking its devices as cards loses the one thing it is for —
 * where things ARE. It opens the canvas at its HEIGHT fill on a narrow screen and
 * pans sideways instead, so `resolveNarrowSurface` below is the question it asks;
 * the grid keeps the list.
 *
 * An opening view is not a floor: the height fill is a CLOSER view than the fit
 * (owner: "you must be able to zoom out of the layout on mobile.. why not let
 * zoom out to fit"), so the fit stays the minimum scale the user can zoom out to,
 * and the canvas simply starts from the fill.
 */

import { field, wireObject } from './wire';

export type DashboardLayoutMode = 'canvas' | 'list';

/** Below this width the viewport is a phone. */
export const PHONE_MAX_WIDTH = 768;
/** Below this height it is a phone held sideways — 10 authored rows cannot fit. */
export const PHONE_MAX_HEIGHT = 500;
/** At or above this width the canvas always has room (tablet landscape, desktop). */
export const DESKTOP_MIN_WIDTH = 1024;

/** Minimum card height on the single-column surface — no sub-100px widget boxes. */
export const LIST_CARD_MIN_HEIGHT = 160;
/** Height one authored row contributes to a card on the single-column surface. */
export const LIST_ROW_HEIGHT = 90;
/** Tallest a single card grows, however large its authored rowSpan is. */
export const LIST_CARD_MAX_HEIGHT = 520;

export interface DashboardLayoutInput {
  width: number;
  portrait: boolean;
  /** Optional: a sideways phone is caught by height even when it is wide. */
  height?: number;
}

export function resolveDashboardLayoutMode({ width, portrait, height }: DashboardLayoutInput): DashboardLayoutMode {
  if (!Number.isFinite(width) || width <= 0) return 'canvas';
  if (width < PHONE_MAX_WIDTH) return 'list';
  if (Number.isFinite(height) && (height as number) > 0 && (height as number) < PHONE_MAX_HEIGHT) return 'list';
  if (portrait && width < DESKTOP_MIN_WIDTH) return 'list';
  return 'canvas';
}

/**
 * The same rule, asked as a yes/no: is this screen too small to paint a
 * reference-space canvas legibly? `resolveDashboardLayoutMode` uses it to pick
 * the list surface; the floorplan uses it to choose its OPENING VIEW (height fill
 * + sideways pan) rather than swapping the surface out.
 */
export function resolveNarrowSurface(input: DashboardLayoutInput): boolean {
  return resolveDashboardLayoutMode(input) === 'list';
}

export interface CanvasFitInput {
  viewportWidth: number;
  viewportHeight: number;
  referenceWidth: number;
  referenceHeight: number;
}

export interface CanvasView {
  scale: number;
  offsetX: number;
  offsetY: number;
}

export interface CanvasOpeningViewInput extends CanvasFitInput {
  /** Narrow screen: open on the height fill, let the user pan sideways. */
  fillHeight: boolean;
}

function measurable({
  viewportWidth,
  viewportHeight,
  referenceWidth,
  referenceHeight,
}: CanvasFitInput): boolean {
  return viewportWidth > 0 && viewportHeight > 0 && referenceWidth > 0 && referenceHeight > 0;
}

/**
 * The FIT: the largest view that still shows the whole plan inside the wrapper
 * (card t_9adb47cf). It is the zoom-out FLOOR — `useZoomPan` takes the fit as its
 * minimum scale, so the user can always pull back to the entire plan — and the
 * target of the fit-to-screen control. Offsets centre the plan.
 */
export function computeCanvasFit({
  viewportWidth,
  viewportHeight,
  referenceWidth,
  referenceHeight,
}: CanvasFitInput): CanvasView {
  if (!measurable({ viewportWidth, viewportHeight, referenceWidth, referenceHeight })) {
    return { scale: 1, offsetX: 0, offsetY: 0 };
  }
  const scale = Math.min(viewportWidth / referenceWidth, viewportHeight / referenceHeight);
  return {
    scale,
    offsetX: (viewportWidth - referenceWidth * scale) / 2,
    offsetY: (viewportHeight - referenceHeight * scale) / 2,
  };
}

/**
 * Where the canvas OPENS: the fit on a screen with room to show the plan
 * legibly, and the HEIGHT FILL on a narrow one — `max(contain, height fill)`, so
 * the plan comes out exactly as tall as the wrapper with the horizontal overflow
 * pan-able (owner's report: the plan was replaced by a card list on phones).
 *
 * `fillHeight` only ever RAISES the scale, so the opening view is never further
 * out than the fit; zooming out to the whole plan stays available as a separate
 * move (the fit is the floor), it is simply not where the view starts.
 */
export function computeCanvasOpeningView({
  viewportWidth,
  viewportHeight,
  referenceWidth,
  referenceHeight,
  fillHeight,
}: CanvasOpeningViewInput): CanvasView {
  const contain = computeCanvasFit({ viewportWidth, viewportHeight, referenceWidth, referenceHeight });
  if (!fillHeight) return contain;
  if (!measurable({ viewportWidth, viewportHeight, referenceWidth, referenceHeight })) return contain;
  const scale = Math.max(contain.scale, viewportHeight / referenceHeight);
  return {
    scale,
    offsetX: (viewportWidth - referenceWidth * scale) / 2,
    offsetY: (viewportHeight - referenceHeight * scale) / 2,
  };
}

export interface GridPlacement {
  col: number;
  row: number;
  colSpan: number;
  rowSpan: number;
}

/**
 * The authored placement of a widget. This is the ONE reader of the col/row
 * spellings: `widgetConfig` is what the editor writes, the row columns are what
 * older rows carry, and the canvas projection plus the mobile ordering must
 * agree on which wins or the two surfaces show different dashboards.
 */
export function readWidgetPlacement(widget: unknown): GridPlacement {
  const config = wireObject(field(widget, 'widgetConfig')) ?? {};
  const num = (...values: unknown[]): number | null => {
    for (const value of values) {
      const parsed = Number(value);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return null;
  };
  return {
    col: num(config.col, field(widget, 'positionX')) ?? 1,
    row: num(config.row, field(widget, 'positionY')) ?? 1,
    colSpan: num(config.colSpan, field(widget, 'colSpan')) ?? 1,
    rowSpan: num(config.rowSpan, field(widget, 'rowSpan')) ?? 1,
  };
}

/** Reading order on the single-column surface: top row first, then left to right. */
export function orderWidgetsForList<T>(widgets: T[]): T[] {
  return widgets
    .map((widget, index) => ({ widget, index, placement: readWidgetPlacement(widget) }))
    .sort((a, b) => a.placement.row - b.placement.row || a.placement.col - b.placement.col || a.index - b.index)
    .map((entry) => entry.widget);
}

/** Height of one card on the single-column surface, derived from its authored rowSpan. */
export function listCardHeight(rowSpan: number): number {
  const span = Number.isFinite(rowSpan) && rowSpan > 0 ? Math.floor(rowSpan) : 1;
  return Math.min(LIST_CARD_MAX_HEIGHT, Math.max(LIST_CARD_MIN_HEIGHT, span * LIST_ROW_HEIGHT));
}

export interface CanvasPlacement {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The authored placement of a canvas widget (floorplan), in reference-space px.
 * `widgetConfig.x/y/w/h` is what the layout paints with (`widgetCanvasPosition` /
 * `widgetCanvasSize` read the same keys); the row columns carry the same numbers
 * for older rows. An unplaced widget reads 0 — the top of the list — mirroring the
 * grid, whose unplaced widget falls back to cell 1. (The canvas would drop such a
 * widget in the centre of the reference space; a one-column list cannot say
 * "centre", and hiding it at the bottom of the scroll would be worse.)
 */
export function readCanvasPlacement(widget: unknown): CanvasPlacement {
  const config = wireObject(field(widget, 'widgetConfig')) ?? {};
  const num = (...values: unknown[]): number | null => {
    for (const value of values) {
      const parsed = Number(value);
      if (Number.isFinite(parsed) && parsed >= 0) return parsed;
    }
    return null;
  };
  return {
    x: num(config.x, field(widget, 'positionX')) ?? 0,
    y: num(config.y, field(widget, 'positionY')) ?? 0,
    w: num(config.w, field(widget, 'colSpan')) ?? 0,
    h: num(config.h, field(widget, 'rowSpan')) ?? 0,
  };
}

/** Reading order on the floorplan's single-column surface: top edge, then left edge. */
export function orderCanvasWidgetsForList<T>(widgets: T[]): T[] {
  return widgets
    .map((widget, index) => ({ widget, index, placement: readCanvasPlacement(widget) }))
    .sort((a, b) => a.placement.y - b.placement.y || a.placement.x - b.placement.x || a.index - b.index)
    .map((entry) => entry.widget);
}

/**
 * Height of one floorplan card on the single-column surface, derived from the
 * canvas height the author gave the widget. The reference space is authored at
 * 1920 px on its long axis, so an authored height is the same order of magnitude
 * as a phone card: it is used as the card's floor, kept above
 * LIST_CARD_MIN_HEIGHT so a 115 px tile is still a card, and capped at
 * LIST_CARD_MAX_HEIGHT so one authored 600 px widget cannot fill the screen.
 */
export function listCardHeightForCanvas(canvasHeight: number): number {
  const authored = Number.isFinite(canvasHeight) && canvasHeight > 0 ? Math.round(canvasHeight) : 0;
  return Math.min(LIST_CARD_MAX_HEIGHT, Math.max(LIST_CARD_MIN_HEIGHT, authored));
}
